/**
 * @file evacuationCapacityModel.js
 * @version 1.0.0
 * @date October 10, 2026
 *
 * @description
 * Ground evacuation capacity from distance, route surface, handling time and the working day
 * (docs/documentation/SPEC_EVAC_CAPACITY.md). PURE functions; parameters in constants/evacuationCapacityParameters.js.
 *
 *   speed (km/h)        = class speed on the route surface x route modifiers (status, weather)
 *   cycle (min)         = 2 x distance / speed x 60 + load + unload + supplies
 *   working minutes/day = crews x shift hours x 60 - crew changes - refuelling
 *   runs per day        = floor( working minutes / cycle )          (no artificial cap: the day is the limit)
 *   patients per day    = vehicles x runs x patients per run
 *
 * Missing or invalid inputs THROW (APP_RULES 6.1). A leg with a closed route has speed 0 and capacity 0.
 */
import {
  GROUND_SPEED_KPH,
  GROUND_HANDLING_MIN,
  GROUND_DAILY_SCHEDULE,
  GROUND_LOADING_MIX,
  GROUND_VEHICLE_CLASSES,
  VEHICLE_CLASS_BY_CATEGORY
} from '../../constants/evacuationCapacityParameters.js';

const WHERE = 'evacuationCapacityModel';

const requireNonNegative = (v, name) => {
  if (typeof v !== 'number' || Number.isNaN(v) || v < 0) throw new Error(`${WHERE}: ${name} must be a number >= 0 (got: ${v})`);
  return v;
};

export const vehicleClassFor = (category) => {
  const cls = VEHICLE_CLASS_BY_CATEGORY[String(category).toUpperCase()];
  if (!cls) throw new Error(`${WHERE}: unknown vehicle category '${category}' (valid: ${Object.keys(VEHICLE_CLASS_BY_CATEGORY).join(', ')})`);
  return cls;
};

/** Base speed (km/h) of a vehicle class on a route surface. */
export const groundBaseSpeedKph = (vehicleClass, surface) => {
  if (!Object.values(GROUND_VEHICLE_CLASSES).includes(vehicleClass)) {
    throw new Error(`${WHERE}: unknown vehicle class '${vehicleClass}' (valid: ${Object.values(GROUND_VEHICLE_CLASSES).join(', ')})`);
  }
  const speed = GROUND_SPEED_KPH[vehicleClass][surface];
  if (typeof speed !== 'number') {
    throw new Error(`${WHERE}: unknown route surface '${surface}' (valid: ${Object.keys(GROUND_SPEED_KPH.LIGHT).join(', ')})`);
  }
  return speed;
};

/** Minutes of one complete run: out, load, back, unload, supplies. */
export const groundCycleMinutes = ({ distanceKm, speedKph }) => {
  requireNonNegative(distanceKm, 'distanceKm');
  requireNonNegative(speedKph, 'speedKph');
  if (speedKph === 0) return Infinity;
  const h = GROUND_HANDLING_MIN;
  const handling = h.load + h.unload + (h.suppliesEveryRun ? h.supplies : 0);
  return (2 * distanceKm / speedKph) * 60 + handling;
};

/** Working minutes of one vehicle in a day, after crew changes and refuelling. */
export const groundWorkingMinutesPerDay = (refuelsPerDay = GROUND_DAILY_SCHEDULE.minRefuelsPerVehiclePerDay, schedule = GROUND_DAILY_SCHEDULE) => {
  const total = schedule.crewsPerVehicle * schedule.shiftHours * 60;
  const fixed = schedule.crewsPerVehicle * schedule.crewChangeMin + refuelsPerDay * schedule.refuelMin;
  return Math.max(0, total - fixed);
};

/**
 * Runs one vehicle can make in a day on a leg.
 * @param {{distanceKm:number, vehicleClass:string, surface:string, speedModifier?:number}} p
 *   speedModifier: route status and weather modifiers (1 = unhindered, 0 = closed)
 */
export const groundRunsPerDay = ({ distanceKm, vehicleClass, surface, speedModifier = 1, speedCapKph = Infinity, rangeKm = Infinity }) => {
  requireNonNegative(speedModifier, 'speedModifier');
  // A vehicle cannot exceed its own cruise speed (ambulanceTypes.js performance.cruise_speed_kmh).
  const speed = Math.min(groundBaseSpeedKph(vehicleClass, surface), speedCapKph) * speedModifier;
  const cycle = groundCycleMinutes({ distanceKm, speedKph: speed });
  if (!Number.isFinite(cycle)) return 0;
  // Refuelling stops: at least the daily minimum, more when the distance driven exceeds the vehicle's range. The runs
  // depend on the refuelling time and the refuelling on the runs, so a few passes settle it.
  let refuels = GROUND_DAILY_SCHEDULE.minRefuelsPerVehiclePerDay;
  let runs = Math.floor(groundWorkingMinutesPerDay(refuels) / cycle);
  for (let i = 0; i < 5; i += 1) {
    const needed = Math.max(GROUND_DAILY_SCHEDULE.minRefuelsPerVehiclePerDay, Math.ceil((runs * 2 * distanceKm) / rangeKm));
    if (needed === refuels) break;
    refuels = needed;
    runs = Math.floor(groundWorkingMinutesPerDay(refuels) / cycle);
  }
  return runs;
};

/** Patients carried per run from a vehicle's litter and sitting places. */
export const groundPatientsPerRun = ({ litterPlaces, ambulatoryPlaces }) => {
  requireNonNegative(litterPlaces, 'litterPlaces');
  requireNonNegative(ambulatoryPlaces, 'ambulatoryPlaces');
  return Math.round(litterPlaces * GROUND_LOADING_MIX.litter + ambulatoryPlaces * GROUND_LOADING_MIX.ambulatory);
};

/**
 * Daily capacity of a group of identical vehicles on a leg.
 * @returns {{runsPerDay:number, patientsPerRun:number, patientsPerDay:number, cycleMinutes:number}}
 */
export const groundGroupCapacity = ({
  vehicles, availability = 1, distanceKm, vehicleClass, surface, speedModifier = 1,
  speedCapKph = Infinity, rangeKm = Infinity,
  litterPlaces, ambulatoryPlaces, mixedLitter = null, mixedAmbulatory = null
}) => {
  requireNonNegative(vehicles, 'vehicles');
  requireNonNegative(availability, 'availability');
  const runsPerDay = groundRunsPerDay({ distanceKm, vehicleClass, surface, speedModifier, speedCapKph, rangeKm });
  // Planning load per run: the vehicle's mixed load from the constants when it has one, else the fallback loading rule.
  const patientsPerRun = (mixedLitter !== null && mixedAmbulatory !== null && (mixedLitter + mixedAmbulatory) > 0)
    ? mixedLitter + mixedAmbulatory
    : groundPatientsPerRun({ litterPlaces, ambulatoryPlaces });
  const speed = Math.min(groundBaseSpeedKph(vehicleClass, surface), speedCapKph) * speedModifier;
  return {
    runsPerDay,
    patientsPerRun,
    patientsPerDay: vehicles * availability * runsPerDay * patientsPerRun,
    cycleMinutes: groundCycleMinutes({ distanceKm, speedKph: speed })
  };
};
